import { memo } from 'react';
import { format } from 'date-fns';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import HelpIcon from '@mui/icons-material/Help';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import Bar from '@components/Charts/Bar';
import { I18N_DATE_FORMAT, getChartDateFormat } from '@helpers/dates';
import { useTranslation } from '@hooks';
import { styled } from '@mui/system';

const PaperStyled = styled(Paper)({
    width: '100%',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    marginBottom: '16px',
    marginTop: 0,
    '@media print': {
        breakInside: 'avoid',
    },
});

const ChartWrapper = styled('div')({
    position: 'relative',
    height: '280px',
    '@media (max-width: 1024px)': {
        height: '400px',
        width: '900px',
    },
    '@media print': {
        height: '280px !important',
    },
});

const createChartData = (labels, data, legends) => {
    return {
        labels,
        datasets: [
            {
                type: 'line',
                yAxisID: 'line',
                label: legends[1],
                data: data.map(i => i?.avgRating),
                backgroundColor: '#1F4C5C',
                hoverBackgroundColor: '#1F4C5C',
                borderColor: '#1F4C5C',
                hoverBorderColor: '#1F4C5C',
                fill: false,
                borderWidth: 2,
                pointRadius: 6,
                pointHoverRadius: 12,
                pointBackgroundColor: '#BCC9CE',
                pointBorderColor: '#1F4C5C',
                pointBorderWidth: 1,
            },
            {
                type: 'bar',
                yAxisID: 'bar',
                label: legends[0],
                data: data.map(i => i?.totalReviews),
                backgroundColor: '#A6D3B9',
                hoverBackgroundColor: '#A6D3B9',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 8,
            },
        ],
    };
};

const createChartOptions = (totalReviews, averageRating) => {
    return {
        elements: {
            point: {
                radius: 0,
            },
            line: {
                tension: 0.4,
                pointStyle: 'circle',
            },
        },
        plugins: {
            chartAreaBorder: null,
        },
        scales: {
            y: {
                display: false,
            },
            bar: {
                type: 'linear',
                position: 'left',
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.3)',
                },
                grid: {
                    display: false,
                    tickLength: 0,
                    drawTicks: false,
                },
                ticks: {
                    stepSize: 5,
                    font: {
                        family: 'Rubik',
                        size: 10,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    padding: 12,
                },
                title: {
                    display: true,
                    position: 'left',
                    align: 'center',
                    font: {
                        family: 'Rubik',
                        size: 10,
                        weight: 500,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    text: totalReviews.toUpperCase(),
                    padding: {
                        bottom: 8,
                    },
                },
            },
            line: {
                type: 'linear',
                position: 'right',
                ticks: {
                    stepSize: 1,
                    font: {
                        family: 'Rubik',
                        size: 10,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    padding: 10,
                },
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.3)',
                    dash: [5, 5],
                },
                grid: {
                    tickLength: 0,
                    drawTicks: false,
                },
                title: {
                    display: true,
                    position: 'left',
                    align: 'center',
                    font: {
                        family: 'Rubik',
                        size: 10,
                        weight: 500,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    text: averageRating.toUpperCase(),
                    padding: {
                        bottom: 14,
                    },
                },
            },
        },
    };
};

const RatingsTimeline = memo(({ data, language, grouping }) => {
    const { t } = useTranslation();

    const labels = data.map(({ dateLabel }) =>
        format(dateLabel, getChartDateFormat(grouping), { locale: I18N_DATE_FORMAT[language] }),
    );
    const legends = [t('AnalyticsReviews.totalReviews'), t('AnalyticsReviews.averageRating')];

    return (
        <PaperStyled elevation={1}>
            <Typography
                variant="title1"
                sx={{ color: 'rgba(0,0,0, 0.87)', marginBottom: '50px', display: 'flex', alignItems: 'center' }}
            >
                {t('AnalyticsReviews.ratingsTimeline')}
                <IconButton sx={{ padding: '4px !important', marginLeft: '4px' }}>
                    <Tooltip title={t('AnalyticsReviews.ratingsTimelineTooltip')} placement="top" enterTouchDelay={0}>
                        <HelpIcon sx={{ height: '18px !important', width: '18px !important' }} />
                    </Tooltip>
                </IconButton>
            </Typography>
            <TableContainer>
                <ChartWrapper id="ratings-timeline-chart-wrapper">
                    {data.length > 0 && (
                        <Bar
                            parentId="ratings-timeline-chart-wrapper"
                            data={createChartData(labels, data, legends)}
                            labels={labels}
                            optionsProps={createChartOptions(
                                t('AnalyticsReviews.totalReviews'),
                                t('AnalyticsReviews.averageRating'),
                            )}
                        />
                    )}
                </ChartWrapper>
            </TableContainer>
        </PaperStyled>
    );
});

RatingsTimeline.displayName = 'RatingsTimeline';

export default RatingsTimeline;
